<script setup lang="ts">
import SimpleTable from '@/components/tables/SimpleTable.vue';
import { RouteName } from '@/router/models/route-name';
import { overlaySpinnerService } from '@/services/app/overlay-spinner-service';
import { entraAuthService } from '@/services/auth/entra-auth-service.ts';
import { supplierInvoiceService } from '@/services/invoices/supplier-invoice-service';
import { projectService } from '@/services/project/project-service';
import { toProjectDisplayName } from '@/utils/projects/toProjectDisplayName';
import type { BaseColumnData } from '@/utils/tables/column-data';
import { sortData } from '@/utils/tables/sorting-data';
import {
  PhArrowsSplit,
  PhFiles,
  PhLetterCircleP,
  PhX,
} from '@phosphor-icons/vue';
import { hasRole, models, utils } from 'cesg-shared';
import { UserRole } from 'cesg-shared/dist/models';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

interface FetchedData {
  projects: Awaited<ReturnType<typeof projectService.getAll>>;
  notifications: Awaited<
    ReturnType<typeof supplierInvoiceService.getSupplierInvoiceNotifications>
  >;
}

const { t } = useI18n();

const router = useRouter();
const fetchedData = ref<FetchedData>({
  projects: [],
  notifications: [],
});
const data = computed(() => {
  const mapped = mapToFormattedData(fetchedData.value.projects);
  return sortData(mapped, 'fortnoxId', 'desc');
});
const currentUser: models.GetMeResponse | null = entraAuthService.me.value;
const noProjects = ref<boolean>(false);

const columns = computed(() => {
  const allColumns: BaseColumnData[] = [
    {
      key: 'name',
      label: t('projects.table.name'),
      sortable: true,
      columnWidth: '10%',
    },
    {
      key: 'status',
      label: t('projects.table.status'),
      sortable: true,
      columnWidth: '5%',
    },
    {
      key: 'type',
      label: t('projects.table.type'),
      sortable: true,
      columnWidth: '5%',
    },
    {
      key: 'earnings',
      label: t('projects.table.earnings'),
      align: 'has-text-right',
      format: utils.formatNumber,
      sortable: true,
      columnWidth: '10%',
    },
    {
      key: 'sekPerM2',
      label: t('projects.table.sekPerM2'),
      align: 'has-text-right',
      format: utils.formatNumber,
      sortable: true,
    },
    {
      key: 'sekPerM2Ata',
      label: t('projects.table.sekPerM2Ata'),
      align: 'has-text-right',
      format: utils.formatNumber,
      sortable: true,
    },
    {
      key: 'compensationForm',
      label: t('projects.table.compensationForm'),
      sortable: true,
    },
    {
      key: 'contractSum',
      label: t('projects.table.contractSum'),
      align: 'has-text-right',
      format: utils.formatNumber,
      sortable: true,
    },
    {
      key: 'ataSum',
      label: t('projects.table.ataSum'),
      align: 'has-text-right',
      format: utils.formatNumber,
      sortable: true,
    },
    {
      key: 'undertakingSum',
      label: t('projects.table.undertakingSum'),
      align: 'has-text-right',
      format: utils.formatNumber,
      sortable: true,
    },
    {
      key: 'startDate',
      label: t('projects.table.startDate'),
      sortable: true,
    },
    {
      key: 'endDate',
      label: t('projects.table.endDate'),
      sortable: true,
    },
    {
      key: 'cash',
      label: t('projects.table.cash'),
      align: 'has-text-right',
      format: utils.formatNumber,
      sortable: true,
    },
    {
      key: 'invoiced',
      label: t('projects.table.invoiced'),
      align: 'has-text-right',
      format: utils.formatNumber,
      sortable: true,
    },
  ];

  return allColumns.filter((col) => {
    if (col.key === 'earnings') {
      return currentUser ? hasRole(UserRole.CESG, currentUser) : false;
    }
    return true;
  });
});

const mapToFormattedData = (
  projects: Awaited<ReturnType<typeof projectService.getAll>>,
) => {
  return projects.map((project) => ({
    ...project,
    name: toProjectDisplayName(project),
  }));
};

const loadData = async () => {
  overlaySpinnerService.show();
  try {
    noProjects.value = false;
    const [projects, notifications] = await Promise.all([
      projectService.getAll(),
      supplierInvoiceService.getSupplierInvoiceNotifications(),
    ]);
    noProjects.value = projects.length === 0;

    fetchedData.value = {
      projects,
      notifications,
    };
  } finally {
    overlaySpinnerService.hide();
  }
};

const navigateToOverview = (id: number) => {
  router.push({ name: RouteName.Overview, params: { id: id } });
};

const navigateToReviewsByType = (id: string, type: string) => {
  const invoiceIds = fetchedData.value.notifications
    .filter((r) => r.projectId === id && r.type === type)
    .map((r) => r.invoiceId);

  if (invoiceIds.length === 0) {
    return;
  }

  if (invoiceIds.length === 1) {
    const [invoiceId] = invoiceIds;
    router.push({
      name: RouteName.InvoiceDetails,
      params: { id, invoiceId },
    });
    return;
  }

  router.push({ name: RouteName.Invoices, params: { id } });
};

const notificationTypeConfigs = [
  {
    type: 'PENDING_REVIEW' as const,
    icon: PhArrowsSplit,
    weight: 'bold' as const,
    colorClass: 'chip--review',
    cornerClass: 'stack-top',
    tooltipKey: 'projects.table.notificationTooltipPendingReview',
    labelKey: 'projects.table.notificationLabelPendingReview',
  },
  {
    type: 'CONTESTED' as const,
    icon: PhX,
    weight: 'bold' as const,
    colorClass: 'chip--contested',
    cornerClass: 'stack-middle',
    tooltipKey: 'projects.table.notificationTooltipContested',
    labelKey: 'projects.table.notificationLabelContested',
  },
  {
    type: 'PARKED' as const,
    icon: PhLetterCircleP,
    weight: 'bold' as const,
    colorClass: 'chip--parked',
    cornerClass: 'stack-bottom',
    tooltipKey: 'projects.table.notificationTooltipParked',
    labelKey: 'projects.table.notificationLabelParked',
  },
];

const getNotificationChips = (projectId: string) => {
  const notifications = fetchedData.value.notifications.filter(
    (r) => r.projectId === projectId,
  );
  return notificationTypeConfigs
    .map((config) => ({
      ...config,
      count: notifications.filter((r) => r.type === config.type).length,
    }))
    .filter((chip) => chip.count > 0);
};

onMounted(loadData);
</script>
<template>
  <div class="mt-4">
    <div class="is-full-width is-flex is-justify-content-space-between">
      <h1 class="is-size-4 mb-4">{{ t('projects.title') }}</h1>
    </div>
    <SimpleTable
      :columns="columns"
      :data="data"
      :row-clickable="true"
      :enable-search="true"
      :sum-column-keys="[
        'earnings',
        'contractSum',
        'ataSum',
        'undertakingSum',
        'cash',
        'invoiced',
      ]"
    >
      <template #tbody="{ data }">
        <tr
          @click="navigateToOverview(row.id)"
          v-for="row in data"
          :key="row.id"
        >
          <td
            v-for="(column, index) in columns"
            :key="index"
            :class="[
              column.align ? column.align : '',
              column.class ? column.class(row[column.key]) : '',
              column.key === 'reviewCount' ? 'review-count-cell' : '',
            ]"
          >
            <span v-if="column.format">
              {{ column.format(row[column.key]) }}
              <span v-if="column.key === 'earnings'"
                ><span class="no-break"
                  >({{
                    utils.formatNumberWithDecimal(row['earningsPercent'])
                  }}
                  %)</span
                ></span
              >
            </span>
            <span v-else-if="column.key === 'name'">
              <span class="name-cell">
                {{ row[column.key] }}
                <span
                  v-if="
                    fetchedData.notifications.some(
                      (r) => r.projectId === row.id,
                    )
                  "
                  class="review-pill-group"
                  data-test="available-reviews-pill"
                >
                  <span class="invoice-outer-pill">
                    <PhFiles :size="16" weight="bold" class="invoice-icon" />
                    <button
                      v-for="chip in getNotificationChips(row.id)"
                      :key="chip.type"
                      class="status-pill"
                      :class="chip.colorClass"
                      :tooltip-text="t(chip.tooltipKey, { count: chip.count })"
                      @click.stop="navigateToReviewsByType(row.id, chip.type)"
                    >
                      <component
                        :is="chip.icon"
                        :size="14"
                        :weight="chip.weight"
                      />
                      <span class="status-pill-label">{{
                        t(chip.labelKey)
                      }}</span>
                      {{ chip.count }}
                    </button>
                  </span>
                </span>
              </span>
            </span>
            <span v-else>{{ row[column.key] }}</span>
          </td>
        </tr>
      </template>
    </SimpleTable>
    <div v-if="noProjects" class="notification is-info no-projects">
      {{ t('projects.noProjectsMessage') }}
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@/assets/style/variables';

.no-projects {
  width: fit-content;
}

.name-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
}

.review-pill-group {
  display: inline-flex;
  gap: 2px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
}

.invoice-outer-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background-color: #f1f5f9;
  border-radius: 999px;
  padding: 4px 9px 4px 8px;
}

.invoice-icon {
  color: #64748b;
  flex-shrink: 0;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 999px;
  padding: 3px 8px 3px 6px;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  border: none;
  cursor: pointer;
  color: var(--white);
  transition: filter 0.1s ease-in-out;

  &:hover {
    filter: brightness(1.15);
  }

  &.chip--review {
    background-color: var(--notification-review);
  }

  &.chip--contested {
    background-color: var(--notification-contested);
  }

  &.chip--parked {
    background-color: var(--notification-parked);
  }
}
</style>
